import React, { useEffect, useState } from 'react';
import _round from 'lodash/round';
import { BarChart, Bar, XAxis, YAxis } from 'recharts';

import RecentPurchases from './RecentPurchases';
import { AdSidebarComponent } from './AdSidebarComponent';

import { colors } from 'styles/settings';
import { EarningsEntry, getDashboardEarningsData } from 'common/api/users/dashboard';
import { useApi } from 'common/modules/async-actions/useApi';
import { useWindowInnerSize } from 'hooks/useWindowSize';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Routes } from 'routes';
import { FeatureFlag } from 'components/feature-flag';

const Container = 'div';
export interface CustomLabelProps {
    x?: number;
    y?: number;
    fill: string;
    value?: number;
    width?: number;
}
export interface CustomXAxis extends EarningsEntry {
    monthsLabel: string;
}

const CustomizedLabel = (props: CustomLabelProps) => {
    const { x, y, value = 0, width, fill } = props;
    return (
        <text
            x={x}
            y={y}
            dy={-5}
            dx={width ? width - 64.5 : 24}
            fontSize="12"
            fontFamily="sans-serif"
            fill={fill}
            textAnchor="start"
        >
            {`$ ${_round(value, 2).toFixed(2)}`}
        </text>
    );
};

const HeaderAndAdSection = () => {
    return (
        <Container className="flex flex-col w-full md:w-48">
            <p className="text-brand-dark text-3xl mb-5 font-light sm:font-normal">
                Your Dashboard
            </p>
            <AdSidebarComponent />
        </Container>
    );
};

const EarningsAndPurchasesSection = () => {
    const [earningData, setEarningData] = useState<Array<CustomXAxis>>([]);
    const [loadEarningsData] = useApi(getDashboardEarningsData);
    const { width } = useWindowInnerSize();

    useEffect(() => {
        loadEarningsData().then(({ data }) => {
            if (data) {
                setEarningData(
                    data.map((dt) => ({
                        ...dt,
                        monthsLabel: dt.months !== 12 ? `${dt.months} mo` : `1 yr`,
                    }))
                );
                return;
            }
        });
    }, [loadEarningsData]);

    return (
        <Container className="py-2 w-full md:pl-20">
            <div
                className="parti_dash_earnings_summary max-w-full mb-12"
                style={{ width: width > 800 ? width - width * 0.45 : width }}
            >
                <div className="flex flex-col sm:flex-row justify-between py-2">
                    <h6 className="text-brand-dark font-semibold text-lg pb-2 sm:pb-0">
                        Earnings Summary
                    </h6>
                </div>
                <div
                    className="max-w-3/5 mt-2 pt-1 pb-0"
                    style={{ borderWidth: 0.5, borderColor: '#eaeaea', borderRadius: 5 }}
                >
                    {!earningData?.length ? (
                        <p className="p-4" />
                    ) : (
                        <>
                            <BarChart
                                width={width > 800 ? width - width * 0.45 : width - 70}
                                height={200}
                                data={earningData.sort((a, b) => a.months - b.months)}
                                className="max-w-full"
                            >
                                <XAxis tickLine dataKey="monthsLabel" fontSize="12" />
                                <YAxis padding={{ top: 20 }} hide />
                                <Bar
                                    dataKey="value"
                                    barSize={width > 800 ? 90 : undefined}
                                    fill={colors.brandDark}
                                    radius={[5, 5, 0, 0]}
                                    label={<CustomizedLabel fill={colors.brandDark} />}
                                />
                            </BarChart>
                        </>
                    )}
                </div>
            </div>
            <FeatureFlag flagKey="enable_family_reporting">
                <div className="mb-11">
                    <Link
                        to={Routes.FamilyEarningsSummary}
                        target="_blank"
                        style={{ textDecoration: 'none' }}
                        className="cursor-pointer"
                    >
                        <span className="text-brand text-lg underline">Earning Summary Report</span>
                        <FontAwesomeIcon
                            icon={faExternalLink}
                            className="inline-block text-brand ml-2.5"
                        />
                        <div className="text-grey-1">
                            High level look at order count, total card value, and earnings for a
                            timeframe.
                        </div>
                    </Link>
                </div>
            </FeatureFlag>
            <RecentPurchases />
        </Container>
    );
};

export const Dashboard = () => {
    return (
        <Container className="flex flex-col md:flex-row">
            <HeaderAndAdSection />
            <EarningsAndPurchasesSection />
        </Container>
    );
};
